import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Fab,
  CircularProgress,
  Divider,
  Alert,
  Collapse,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Print as PrintIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { mobileChequeService } from '../../../services/mobile/mobileChequeService';
import mobileApi from '../../../services/mobile/mobileApi';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';

const statusColors = {
  Pending: 'warning',
  Printed: 'info',
  Cleared: 'success',
  Cancelled: 'error'
};

const MobileDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentCheques, setRecentCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('all');
  // Currency for aggregate displays: selected bank's currency, or AED when "all".
  const selectedCurrency = selectedBankId === 'all'
    ? 'AED'
    : (banks.find((b) => String(b.Id || b.id) === String(selectedBankId))?.Currency
        || banks.find((b) => String(b.Id || b.id) === String(selectedBankId))?.currency
        || 'AED');

  // PDC Alerts
  const [pdcAlerts, setPdcAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pdcAlertsDismissed');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [expandedAlert, setExpandedAlert] = useState(null);

  useEffect(() => {
    const checkPdcAlerts = async () => {
      try {
        const bankIdParam = selectedBankId !== 'all' ? Number(selectedBankId) : undefined;
        const params = bankIdParam ? { bankId: bankIdParam } : {};
        const response = await mobileApi.get('/pdc-alerts/check', { params });
        const responseData = response.data;
        const data = responseData.Data ?? responseData.data ?? {};
        const hasAlerts = data.HasAlerts ?? data.hasAlerts ?? false;
        const alerts = data.Alerts ?? data.alerts ?? [];
        setPdcAlerts(hasAlerts && alerts.length > 0 ? alerts : []);
      } catch {
        // Silent fail — alerts are non-critical
      }
    };
    checkPdcAlerts();
  }, [selectedBankId]);

  const handleDismissAlert = (alertDays) => {
    const updated = [...dismissedAlerts, alertDays];
    setDismissedAlerts(updated);
    sessionStorage.setItem('pdcAlertsDismissed', JSON.stringify(updated));
  };

  const visibleAlerts = pdcAlerts.filter((a) => {
    const days = a.AlertDays ?? a.alertDays;
    return !dismissedAlerts.includes(days);
  });

  // Load banks once
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const result = await mobileChequeService.getBanks();
        if (result.success) {
          const data = Array.isArray(result.data) ? result.data : [];
          setBanks(data);
        }
      } catch {
        // Silent fail
      }
    };
    loadBanks();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const bankIdParam = selectedBankId !== 'all' ? Number(selectedBankId) : undefined;
        const bankParams = bankIdParam ? { bankId: bankIdParam } : {};

        const [statsResult, chequesResult] = await Promise.all([
          mobileChequeService.getDashboardStats(bankParams),
          mobileChequeService.getCheques({ PageSize: 5, PageNumber: 1, ...(bankIdParam ? { bankId: bankIdParam } : {}) })
        ]);

        if (statsResult.success) {
          setStats(statsResult.data);
        }
        if (chequesResult.success) {
          const cheques = Array.isArray(chequesResult.data) ? chequesResult.data : [];
          setRecentCheques(cheques.slice(0, 5));
        }
      } catch {
        // errors handled by service layer
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedBankId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#2563eb' }} />
      </Box>
    );
  }

  const breakdown = stats?.StatusBreakdown ?? stats?.statusBreakdown ?? {};
  const pending = breakdown.Pending ?? breakdown.pending ?? 0;
  const printed = breakdown.Printed ?? breakdown.printed ?? 0;
  const cleared = breakdown.Cleared ?? breakdown.cleared ?? 0;
  const cancelled = breakdown.Cancelled ?? breakdown.cancelled ?? 0;
  const bounced = breakdown.Bounced ?? breakdown.bounced ?? 0;
  const total = pending + printed + cleared + cancelled + bounced;

  const statCards = [
    { label: 'Total', value: total, color: '#2563eb' },
    { label: 'Pending', value: pending, color: '#f59e0b' },
    { label: 'Printed', value: printed, color: '#3b82f6' },
    { label: 'Cleared', value: cleared, color: '#10b981' }
  ];

  const formatAmount = (amount, currency = 'AED') =>
    formatCurrencyAmount(amount, currency, { withSymbol: false });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
          Dashboard
        </Typography>
        <FormControl size="small" fullWidth>
          <Select
            value={selectedBankId}
            onChange={(e) => setSelectedBankId(e.target.value)}
            displayEmpty
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: 600,
              '& .MuiSelect-select': { py: 0.75, pl: 1.5 },
            }}
          >
            <MenuItem value="all">All Banks</MenuItem>
            {banks.map((bank) => {
              const bankId = bank.Id || bank.id;
              const name = bank.AccountName || bank.accountName || '';
              const currency = bank.Currency || bank.currency || '';
              return (
                <MenuItem key={bankId} value={bankId}>
                  {name} ({currency})
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      {/* PDC Alert Banners */}
      {visibleAlerts.map((alert) => {
        const days = alert.AlertDays ?? alert.alertDays;
        const count = alert.ChequeCount ?? alert.chequeCount ?? 0;
        const total = alert.TotalAmount ?? alert.totalAmount ?? 0;
        const cheques = alert.Cheques ?? alert.cheques ?? [];
        const severity = days <= 7 ? 'error' : days <= 14 ? 'warning' : 'info';
        const formattedTotal = formatCurrencyAmount(total, selectedCurrency, { withSymbol: false });
        const isExpanded = expandedAlert === days;

        return (
          <Box key={days} sx={{ mb: 1.5 }}>
            <Collapse in>
              <Alert
                severity={severity}
                sx={{ alignItems: 'center', '& .MuiAlert-message': { flex: 1 } }}
                action={
                  <IconButton size="small" color="inherit" onClick={() => handleDismissAlert(days)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                  {count} cheque{count !== 1 ? 's' : ''} due within {days} days — Total: {formattedTotal}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit', mt: 0.5, display: 'inline-block' }}
                  onClick={() => setExpandedAlert(isExpanded ? null : days)}
                >
                  {isExpanded ? 'Hide Details' : 'Show Details'}
                </Typography>
              </Alert>
            </Collapse>

            <Collapse in={isExpanded}>
              <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderTop: 0, borderRadius: '0 0 8px 8px' }}>
                {cheques.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>No details available</Typography>
                  </Box>
                ) : (
                  <List disablePadding dense>
                    {[...cheques]
                      .sort((a, b) => (a.DaysFromNow ?? a.daysFromNow ?? 0) - (b.DaysFromNow ?? b.daysFromNow ?? 0))
                      .map((cheque, i) => {
                        const beneficiary = cheque.BeneficiaryName ?? cheque.beneficiaryName ?? '';
                        const amount = cheque.ChqAmount ?? cheque.chqAmount ?? 0;
                        const currency = cheque.Currency ?? cheque.currency ?? '';
                        const chqDate = cheque.ChqDate ?? cheque.chqDate ?? '';
                        const daysLeft = cheque.DaysFromNow ?? cheque.daysFromNow ?? 0;
                        const chqNum = cheque.ChequeNumber ?? cheque.chequeNumber ?? '';

                        return (
                          <Box key={cheque.Id ?? cheque.id ?? i}>
                            <ListItem
                              sx={{ px: 2, py: 1 }}
                              onClick={() => navigate('/mobile/pdc-calendar')}
                              secondaryAction={
                                <Chip
                                  label={daysLeft <= 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : `${daysLeft}d`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    backgroundColor: daysLeft <= 1 ? '#fef2f2' : daysLeft <= 7 ? '#fef3c7' : '#f0f9ff',
                                    color: daysLeft <= 1 ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#2563eb',
                                  }}
                                />
                              }
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 4 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a' }} noWrap>
                                      {beneficiary}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#0f172a', fontFamily: 'monospace', flexShrink: 0, ml: 1 }}>
                                      {currency} {formatAmount(amount, currency)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                                    #{chqNum} · {formatDate(chqDate)}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            {i < cheques.length - 1 && <Divider />}
                          </Box>
                        );
                      })}
                  </List>
                )}
              </Card>
            </Collapse>
          </Box>
        );
      })}

      {/* Stats Grid */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {statCards.map((stat) => (
          <Grid item xs={6} key={stat.label}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                bgcolor: '#fff'
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color, mt: 0.5 }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Cheques */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#0f172a' }}>
        Recent Cheques
      </Typography>

      {recentCheques.length === 0 ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No cheques yet. Create your first cheque!
          </Typography>
        </Card>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <List disablePadding>
            {recentCheques.map((cheque, index) => (
              <Box key={cheque.Id || cheque.id || index}>
                <ListItem
                  sx={{ px: 2, py: 1.5, cursor: 'pointer' }}
                  onClick={() => navigate(`/mobile/cheques/${cheque.Id || cheque.id}`)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                          {cheque.BeneficiaryName || cheque.beneficiaryName || 'Unknown'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb' }}>
                          {formatAmount(cheque.ChqAmount || cheque.chqAmount, cheque.Currency || cheque.currency)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {formatDate(cheque.ChequeDate || cheque.chequeDate)}
                        </Typography>
                        <Chip
                          label={cheque.Status || cheque.status || 'Pending'}
                          size="small"
                          color={statusColors[cheque.Status || cheque.status] || 'default'}
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
                {index < recentCheques.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Card>
      )}

      {/* Quick Print FAB */}
      <Fab
        color="primary"
        onClick={() => navigate('/mobile/new-cheque')}
        sx={{
          position: 'fixed',
          bottom: 72,
          right: 16,
          backgroundColor: '#2563eb',
          '&:hover': { backgroundColor: '#1d4ed8' }
        }}
      >
        <PrintIcon />
      </Fab>
    </Box>
  );
};

export default MobileDashboard;
